import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, ArrowUpRight } from "lucide-react";
import Logo from "./Logo";
import MobileMenu from "./MobileMenu";
import { destinations } from "@/data/destinations";
import { services } from "@/data/services";
import { Icon } from "@/lib/icons";
import { useT } from "@/i18n";

const DestMenu = ({ close }) => (
  <div className="grid grid-cols-3 gap-px bg-ivory/10">
    {destinations.map((d) => (
      <Link key={d.slug} to={`/destinations/${d.slug}`} onClick={close} data-testid={`mega-dest-${d.slug}`} className="group relative h-40 overflow-hidden bg-navy">
        <img src={d.image} alt={`${d.city} skyline`} className="absolute inset-0 h-full w-full object-cover opacity-50 transition-all duration-[1.2s] group-hover:scale-110 group-hover:opacity-70" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-transparent" />
        <div className="absolute inset-x-5 bottom-4 flex items-end justify-between">
          <div>
            <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-gold">{d.city}</p>
            <p className="font-serif text-2xl text-ivory">{d.name}</p>
          </div>
          <ArrowUpRight className="h-5 w-5 text-gold opacity-0 transition-all duration-500 group-hover:opacity-100" />
        </div>
      </Link>
    ))}
  </div>
);

const ServMenu = ({ close }) => (
  <div className="grid grid-cols-[1fr_320px] gap-10">
    <div className="grid grid-cols-2 gap-x-8 gap-y-1">
      {services.map((s) => (
        <Link key={s.slug} to={`/services/${s.slug}`} onClick={close} data-testid={`mega-serv-${s.slug}`} className="group flex items-start gap-4 border-b border-ivory/10 py-4">
          <Icon name={s.icon} className="mt-1 h-5 w-5 shrink-0 text-gold" strokeWidth={1.25} />
          <div>
            <p className="font-serif text-xl text-ivory transition-colors group-hover:text-gold">{s.name}</p>
            <p className="mt-1 text-[13px] leading-relaxed text-ivory/70">{s.short}</p>
          </div>
        </Link>
      ))}
    </div>
    <Link to="/eligibility" onClick={close} className="group relative flex flex-col justify-end overflow-hidden p-7" data-testid="mega-eligibility-card">
      <img src="/img/jet.jpg" alt="Private jet at golden hour" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.4s] group-hover:scale-105" loading="lazy" />
      <div className="absolute inset-0 bg-navy/70" />
      <div className="relative">
        <p className="eyebrow-light">Two minutes</p>
        <p className="mt-3 font-serif text-3xl leading-tight text-ivory">Discover the routes open to you.</p>
        <span className="mt-5 inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.2em] text-gold">Check eligibility <ArrowUpRight className="h-4 w-4" /></span>
      </div>
    </Link>
  </div>
);

export default function Navbar() {
  const { t } = useT();
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(null);
  const [mobile, setMobile] = useState(false);
  const timer = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  useEffect(() => {
    setMenu(null);
    setMobile(false);
  }, [pathname]);

  const open = (m) => { clearTimeout(timer.current); setMenu(m); };
  const leave = () => { timer.current = setTimeout(() => setMenu(null), 160); };
  const solid = scrolled || menu;
  const linkCls = ({ isActive }) => `link-lux text-[12px] font-medium uppercase tracking-[0.2em] transition-colors ${isActive ? "text-gold" : "text-ivory/90 hover:text-gold"}`;

  return (
    <header data-testid="site-navbar" className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,padding] duration-700 ${solid ? "bg-navy/95 py-2 shadow-[0_10px_40px_-20px_rgba(0,0,0,.6)] backdrop-blur-xl" : "bg-transparent py-4"}`} onMouseLeave={leave}>
      <div className="container-lux flex items-center justify-between gap-6">
        <Logo size={scrolled ? "sm" : "md"} />
        <nav className="hidden items-center gap-7 lg:flex xl:gap-9" aria-label="Primary">
          {[["destinations", t("nav.destinations")], ["services", t("nav.services")]].map(([k, label]) => (
            <button key={k} type="button" data-testid={`nav-${k}-trigger`} onMouseEnter={() => open(k)} onFocus={() => open(k)} onClick={() => setMenu(menu === k ? null : k)} aria-expanded={menu === k} className={`flex items-center gap-1.5 text-[12px] font-medium uppercase tracking-[0.2em] transition-colors ${menu === k ? "text-gold" : "text-ivory/90 hover:text-gold"}`}>
              {label} <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-500 ${menu === k ? "rotate-180" : ""}`} />
            </button>
          ))}
          {[["/about", "nav.about"], ["/insights", "nav.insights"], ["/faq", "nav.faq"], ["/contact", "nav.contact"]].map(([to, k]) => (
            <NavLink key={to} to={to} className={linkCls} onMouseEnter={() => setMenu(null)} data-testid={`nav-link-${k.split(".")[1]}`}>{t(k)}</NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/eligibility" className="hidden whitespace-nowrap text-[12px] font-medium uppercase tracking-[0.2em] text-ivory/90 transition-colors hover:text-gold 2xl:inline" data-testid="nav-eligibility-link">{t("cta.eligibilityShort")}</Link>
          <Link to="/contact" className="btn-gold hidden whitespace-nowrap !px-5 !py-3 md:inline-flex" data-testid="nav-book-consultation-btn"><span>{t("cta.bookShort")}</span></Link>
          <button type="button" className="p-2 text-ivory lg:hidden" onClick={() => setMobile(true)} aria-label="Open menu" data-testid="nav-mobile-menu-btn"><Menu className="h-6 w-6" /></button>
        </div>
      </div>
      <AnimatePresence>
        {menu && (
          <motion.div key={menu} onMouseEnter={() => open(menu)} initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.4, ease: [0.2, 0.7, 0.2, 1] }} className="absolute inset-x-0 top-full hidden border-t border-gold/20 bg-navy py-10 shadow-2xl backdrop-blur-xl lg:block" data-testid={`mega-menu-${menu}`}>
            <div className="container-lux">{menu === "destinations" ? <DestMenu close={() => setMenu(null)} /> : <ServMenu close={() => setMenu(null)} />}</div>
          </motion.div>
        )}
      </AnimatePresence>
      <MobileMenu open={mobile} onClose={() => setMobile(false)} />
    </header>
  );
}
